import { MovingElement } from "@repo/video-engine-i2v/components/Element/Element";
import {
  BoundingBox,
  Guidelines,
  HorizontalGuideline,
  PointerState,
  VerticalGuideline,
} from "./element-motion-state";
import type { Set as StoreSet, Get as StoreGet } from "./element-motion-store";
import { EditorStore } from "../editor-store/editor-store";
import { getElementsByScene, getResolution } from "@repo/api/utils/composition";
import { ElementDto, SceneTypeDto } from "@repo/api/overlays";
import { ELEMENT_CONTROLS_ROOT_ID } from "@/features/Editor/Element/ElementControlsLayer";
import {
  RESIZE_HANDLE_ID_PREFIX,
  RESIZE_HANDLE_TYPES,
  ResizeHandleType,
} from "@/features/Editor/Element/ResizeHandle";
import { VIDEO_PREVIEW_CONTAINER_ID } from "@/features/Editor/VideoPreview/VideoPreview";
import { isActiveElementInput, throttle } from "@/lib/utils";
import { ASPECT_RATIO_CONTROLS_ID } from "@/features/Editor/VideoPreview/AspectRatioControls";
import { VIDEO_PREVIEW_CONTROLS_ID } from "@/features/Editor/VideoPreview/VideoPreviewControls/VideoPreviewControls";
import { AlignType } from "@/features/Editor/Element/SelectionActions";

type ElementWithMeta = {
  element: ElementDto;
  sceneType: SceneTypeDto;
  index: number;
};

type ClipboardElement = { element: ElementDto; sceneType: SceneTypeDto };
type ElementRef = { externalId: string; sceneType: SceneTypeDto };

// Pointer downs inside an element marked with this attribute keep the current
// selection so list items can be Cmd/Ctrl-clicked to build a selection.
export const PRESERVE_SELECTION_ATTR = "data-preserve-element-selection";

export class ElementMotionService {
  constructor(set: StoreSet, get: StoreGet) {
    this.set = set;
    this.get = get;
  }

  private set: StoreSet;
  private get: StoreGet;

  private editorStore: EditorStore | null = null;
  private guidelines: Guidelines | null = null;
  private pointerState: PointerState | null = null;
  private arrowMoveTimeout: number | null = null;
  private clipboard: ClipboardElement[] = [];

  private unsubEditorStore: () => void = () => {};

  initialize = (editorStore: EditorStore) => {
    this.editorStore = editorStore;

    this.unsubEditorStore = editorStore.subscribe((state, previousState) => {
      const prevPanelElementId =
        previousState.overlaysTabState.currentElement?.externalId;
      const currentPanelElementId =
        state.overlaysTabState.currentElement?.externalId;

      if (
        currentPanelElementId &&
        !this.get().selectedElementIds.has(currentPanelElementId) &&
        prevPanelElementId !== currentPanelElementId
      ) {
        this.updateSelectedElementIds([currentPanelElementId], "merge");
      }

      const prevAspectRatio = previousState.video?.compositionJson.aspectRatio;
      const currentAspectRatio = state.video?.compositionJson.aspectRatio;

      if (prevAspectRatio !== currentAspectRatio) {
        this.clearSelectionState();
      }

      const elementIds = new Set(
        Object.keys(this.getElementsMapWithSceneAndIndex()),
      );

      const existingElementIds = Array.from(
        this.get().selectedElementIds,
      ).filter((id) => elementIds.has(id));

      this.updateSelectedElementIds(existingElementIds, "replace");
    });

    document.addEventListener("pointerdown", this.handlePointerDown);
    document.addEventListener("pointermove", this.handlePointerMove);
    document.addEventListener("pointerup", this.handlePointerUp);
    document.addEventListener("keydown", this.handleBackspace);
    document.addEventListener("keydown", this.handleArrow);
    document.addEventListener("keydown", this.handleClipboardShortcuts);
  };

  destroy = () => {
    this.unsubEditorStore();

    this.editorStore = null;
    document.removeEventListener("pointerdown", this.handlePointerDown);
    document.removeEventListener("pointermove", this.handlePointerMove);
    document.removeEventListener("pointerup", this.handlePointerUp);
    document.removeEventListener("keydown", this.handleBackspace);
    document.removeEventListener("keydown", this.handleArrow);
    document.removeEventListener("keydown", this.handleClipboardShortcuts);
  };

  handlePointerDown = (event: PointerEvent) => {
    const videoPreviewState = this.editorStore?.getState().videoPreviewState;

    if (
      event.button !== 0 ||
      this.pointerState !== null ||
      videoPreviewState?.isPlaying ||
      videoPreviewState?.isFullscreen
    ) {
      return;
    }

    const isAllowedTarget = this.getIsAllowedPointerDownTarget(event);

    if (!isAllowedTarget) {
      const preservesSelection =
        event.target instanceof Element &&
        event.target.closest(`[${PRESERVE_SELECTION_ATTR}]`) !== null;

      if (this.get().selectedElementIds.size > 0 && !preservesSelection) {
        this.updateSelectedElementIds([], "replace");
      }

      return;
    }

    const scaledCoordinate = this.getScaledCoordinate(event);

    if (scaledCoordinate === null) {
      return;
    }

    const { x, y } = scaledCoordinate;

    if (this.handlePointerDownResize(x, y, event)) {
      return;
    }

    this.handlePointerDownDrag(x, y, event);
  };

  handlePointerMove = throttle((event: PointerEvent) => {
    if (
      this.pointerState === null ||
      !this.pointerState.type.startsWith("POINTER_")
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const scaledCoordinate = this.getScaledCoordinate(event);

    if (scaledCoordinate === null) {
      return;
    }

    const { x, y } = scaledCoordinate;

    if (this.handlePointerMoveResize(x, y, event)) {
      return;
    }

    this.handlePointerMoveDrag(x, y, event);
  }, 15);

  handlePointerUp = (event: PointerEvent) => {
    const pointerType = this.pointerState?.type;

    if (
      pointerType === undefined ||
      !pointerType.startsWith("POINTER_") ||
      event.button !== 0
    ) {
      return;
    }

    if (
      ["POINTER_MOVE_RESIZE", "POINTER_MOVE_SELECTION"].includes(pointerType)
    ) {
      this.commitMovingElements();
    } else if (pointerType === "POINTER_DOWN_SELECTION") {
      this.handlePointerUpDownSelection(event);
    }

    this.handleEditorPanelChange();

    this.updatePointerState(null);
    this.guidelines = null;
    this.set((state) => {
      state.marqueeBoundingBox = null;
      state.movingElements = {};
    });
  };

  handleArrow = throttle((event: KeyboardEvent) => {
    const isAllowedKey = [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
    ].includes(event.key);

    const isAllowedPointerType =
      !this.pointerState || this.pointerState.type === "ARROW_MOVE_SELECTION";

    const videoPreviewState = this.editorStore?.getState().videoPreviewState;

    if (
      !isAllowedKey ||
      isActiveElementInput() ||
      !isAllowedPointerType ||
      videoPreviewState?.isPlaying ||
      videoPreviewState?.isFullscreen ||
      this.get().selectedElementIds.size === 0
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!this.pointerState) {
      const initialSelectionBoundingBox = this.getSelectionBoundingBox();

      if (initialSelectionBoundingBox === null) {
        return;
      }

      this.updatePointerState({
        type: "ARROW_MOVE_SELECTION",
        initialCoordinates: { x: 0, y: 0 },
        latestCoordinates: { x: 0, y: 0 },
        initialSelectionBoundingBox,
      });
      this.setupMovingElements();
      this.setupGuidelines();
    }

    let xOffset = 0;
    let yOffset = 0;

    if (event.key === "ArrowUp") {
      yOffset = -1;
    } else if (event.key === "ArrowDown") {
      yOffset = 1;
    } else if (event.key === "ArrowLeft") {
      xOffset = -1;
    } else if (event.key === "ArrowRight") {
      xOffset = 1;
    }

    const isShiftPressed = event.shiftKey;

    if (isShiftPressed) {
      xOffset *= 10;
      yOffset *= 10;
    }

    const latestCoordinates = this.pointerState!.latestCoordinates;

    this.updatePointerState(
      {
        ...this.pointerState!,
        latestCoordinates: {
          x: latestCoordinates!.x + xOffset,
          y: latestCoordinates!.y + yOffset,
        },
      },
      false,
    );

    const selectionBox = this.calculateSelectionBoxArrow();

    if (selectionBox === null) {
      return;
    }

    this.calculateMovingElementsFromSelectionBox(selectionBox);

    if (this.arrowMoveTimeout) {
      clearTimeout(this.arrowMoveTimeout);
    }

    this.arrowMoveTimeout = setTimeout(() => {
      this.handleArrowCommit();
    }, 500);
  }, 15);

  handleArrowCommit = () => {
    if (this.pointerState?.type !== "ARROW_MOVE_SELECTION") {
      return;
    }

    this.commitMovingElements();
    this.updatePointerState(null);
    this.guidelines = null;
    this.set((state) => {
      state.movingElements = {};
    });
  };

  handleBackspace = (event: KeyboardEvent) => {
    if (
      (event.key !== "Backspace" && event.key !== "Delete") ||
      isActiveElementInput() ||
      this.get().selectedElementIds.size === 0
    ) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    this.deleteSelectedElements();
  };

  handleClipboardShortcuts = (event: KeyboardEvent) => {
    if (!event.metaKey && !event.ctrlKey) return;
    if (event.shiftKey || event.altKey || isActiveElementInput()) return;

    const videoPreviewState = this.editorStore?.getState().videoPreviewState;
    if (videoPreviewState?.isPlaying || videoPreviewState?.isFullscreen) {
      return;
    }

    const key = event.key.toLowerCase();
    const hasSelection = this.get().selectedElementIds.size > 0;

    if (key === "c") {
      // Let native copy win when the user has text selected.
      if (!hasSelection || window.getSelection()?.toString()) return;
      event.preventDefault();
      event.stopPropagation();
      this.copySelectedElements();
    } else if (key === "v") {
      if (this.clipboard.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.pasteElements();
    } else if (key === "d") {
      if (!hasSelection) return;
      event.preventDefault();
      event.stopPropagation();
      this.duplicateSelectedElements();
    } else if (key === "h") {
      if (!hasSelection) return;
      event.preventDefault();
      event.stopPropagation();
      this.toggleSelectedElementsVisibility();
    }
  };

  toggleSelectedElementsVisibility = () => {
    const editorService = this.editorStore?.getState().service;

    if (!editorService) {
      return;
    }

    editorService.toggleElementsVisibility(
      Array.from(this.get().selectedElementIds),
    );
  };

  copySelectedElements = () => {
    const elementsMap = this.getElementsMapWithSceneAndIndex();

    this.clipboard = Array.from(this.get().selectedElementIds)
      .map((id) => elementsMap[id])
      .filter((meta): meta is ElementWithMeta => meta !== undefined)
      .sort((a, b) => a.index - b.index)
      .map(({ element, sceneType }) => ({
        element: structuredClone(element),
        sceneType,
      }));
  };

  pasteElements = () => {
    const editorService = this.editorStore?.getState().service;
    const composition = this.editorStore?.getState().video?.compositionJson;
    const aspectRatio = composition?.aspectRatio;

    if (!editorService || !composition || !aspectRatio) {
      return;
    }

    const pasted: ElementRef[] = [];

    // Each add prepends, so iterate in reverse to keep the copied order.
    for (const { element, sceneType } of [...this.clipboard].reverse()) {
      if (sceneType !== "mainScene" && !composition[sceneType]?.[aspectRatio]) {
        continue;
      }

      const copy: ElementDto = {
        ...structuredClone(element),
        externalId: crypto.randomUUID(),
        position: {
          type: "ABSOLUTE",
          x: element.position.x + 20,
          y: element.position.y + 20,
        },
      };

      editorService.addElementToComposition(
        copy,
        aspectRatio,
        sceneType,
        false,
      );
      pasted.unshift({ externalId: copy.externalId, sceneType });
    }

    this.focusElements(pasted);
  };

  duplicateSelectedElements = () => {
    const editorService = this.editorStore?.getState().service;

    if (!editorService) {
      return;
    }

    const elementsMap = this.getElementsMapWithSceneAndIndex();
    const idsByScene: Record<SceneTypeDto, string[]> = {
      startScene: [],
      mainScene: [],
      endScene: [],
    };

    for (const id of this.get().selectedElementIds) {
      const meta = elementsMap[id];
      if (meta) idsByScene[meta.sceneType].push(id);
    }

    const duplicated: ElementRef[] = [];

    for (const sceneType of ["startScene", "mainScene", "endScene"] as const) {
      for (const externalId of editorService.duplicateElements(
        idsByScene[sceneType],
        sceneType,
      )) {
        duplicated.push({ externalId, sceneType });
      }
    }

    this.focusElements(duplicated);
  };

  // Selects the given elements and, for a single element, opens it in the
  // overlays panel; otherwise closes the element editor.
  private focusElements = (elements: ElementRef[]) => {
    const editorService = this.editorStore?.getState().service;

    this.updateSelectedElementIds(
      elements.map(({ externalId }) => externalId),
      "replace",
    );

    if (elements.length === 1) {
      const { externalId, sceneType } = elements[0]!;
      editorService?.updateOverlaysTabState((state) => {
        state.currentTab = sceneType === "mainScene" ? "elements" : sceneType;
        state.currentElement = { externalId, sceneType };
      });
    } else {
      editorService?.updateOverlaysTabState((state) => {
        state.currentElement = null;
      });
    }
  };

  alignSelectedElements = (alignType: AlignType) => {
    const aspectRatio =
      this.editorStore?.getState().video?.compositionJson.aspectRatio;

    const selectionBoundingBox = this.getSelectionBoundingBox();

    if (!aspectRatio || !selectionBoundingBox) {
      return;
    }

    const resolution = getResolution(aspectRatio);

    let offsetX = 0;
    let offsetY = 0;

    if (alignType === "left") {
      offsetX = -selectionBoundingBox.left;
    } else if (alignType === "right") {
      offsetX = resolution.width - selectionBoundingBox.right;
    } else if (alignType === "top") {
      offsetY = -selectionBoundingBox.top;
    } else if (alignType === "bottom") {
      offsetY = resolution.height - selectionBoundingBox.bottom;
    } else if (alignType === "center") {
      const screenCenter = Math.floor(resolution.width / 2);
      const selectionCenter = Math.floor(
        (selectionBoundingBox.left + selectionBoundingBox.right) / 2,
      );
      offsetX = screenCenter - selectionCenter;
    } else if (alignType === "middle") {
      const screenCenter = Math.floor(resolution.height / 2);
      const selectionCenter = Math.floor(
        (selectionBoundingBox.top + selectionBoundingBox.bottom) / 2,
      );
      offsetY = screenCenter - selectionCenter;
    }

    for (const elementExternalId of this.get().selectedElementIds) {
      this.editorStore?.getState().service.updateElement(
        elementExternalId,
        (element: ElementDto) => {
          element.position.x = Math.round(element.position.x + offsetX);
          element.position.y = Math.round(element.position.y + offsetY);
        },
        false,
        false,
      );
    }

    this.editorStore?.getState().service.syncVideoDebounced();
  };

  deleteSelectedElements = () => {
    const editorService = this.editorStore?.getState().service;
    const elementsMap = this.getElementsMapWithSceneAndIndex();

    for (const id of this.get().selectedElementIds) {
      const element = elementsMap[id];

      if (!element) {
        continue;
      }

      editorService?.deleteElement(id, element.sceneType, false);
    }

    editorService?.syncVideoDebounced();

    this.updateSelectedElementIds([], "replace");
  };

  getBoundingBox = (externalId: string) => {
    return this.get().boundingBoxes[externalId];
  };

  setBoundingBox = (externalId: string, boundingBox: BoundingBox | null) => {
    this.set((state) => {
      if (boundingBox === null) {
        delete state.boundingBoxes[externalId];
      } else {
        state.boundingBoxes[externalId] = boundingBox;
      }
    });
  };

  getElementsMapWithSceneAndIndex = (): Record<string, ElementWithMeta> => {
    const composition = this.editorStore?.getState().video?.compositionJson;

    if (!composition) {
      return {};
    }

    const elementsMap: Record<string, ElementWithMeta> = {};

    [
      ...getElementsByScene(composition, "mainScene").map((element) => ({
        element,
        sceneType: "mainScene" as const,
      })),
      ...getElementsByScene(composition, "startScene").map((element) => ({
        element,
        sceneType: "startScene" as const,
      })),
      ...getElementsByScene(composition, "endScene").map((element) => ({
        element,
        sceneType: "endScene" as const,
      })),
    ].forEach((elementWithMeta, index) => {
      elementsMap[elementWithMeta.element.externalId] = {
        ...elementWithMeta,
        index,
      };
    });

    return elementsMap;
  };

  getSelectionBoundingBox = (): BoundingBox | null => {
    const selectedBoundingBoxes: BoundingBox[] = [];

    for (const id of this.get().selectedElementIds) {
      const boundingBox = this.get().boundingBoxes[id];

      if (boundingBox) {
        selectedBoundingBoxes.push(boundingBox);
      }
    }

    if (selectedBoundingBoxes.length === 0) {
      return null;
    }

    const minTop = Math.min(...selectedBoundingBoxes.map((box) => box.top));
    const maxBottom = Math.max(
      ...Object.values(selectedBoundingBoxes).map((box) => box.bottom),
    );
    const minLeft = Math.min(
      ...Object.values(selectedBoundingBoxes).map((box) => box.left),
    );
    const maxRight = Math.max(
      ...Object.values(selectedBoundingBoxes).map((box) => box.right),
    );

    if (maxBottom - minTop < 1 || maxRight - minLeft < 1) {
      return null;
    }

    return {
      top: minTop,
      bottom: maxBottom,
      left: minLeft,
      right: maxRight,
    };
  };

  getGuidelines = (): Guidelines | null => {
    return this.guidelines;
  };

  getActiveGuidelines = (
    selectionBoundingBox: BoundingBox | null,
  ): (VerticalGuideline | HorizontalGuideline)[] => {
    if (!selectionBoundingBox || !this.guidelines) {
      return [];
    }

    const { left, top, right, bottom } = selectionBoundingBox;
    const xCenter = Math.floor((left + right) / 2);
    const yCenter = Math.floor((top + bottom) / 2);

    const activeGuidelines: (VerticalGuideline | HorizontalGuideline)[] = [];

    for (const x of [left, xCenter, right]) {
      const activeGuideline = this.guidelines.vertical[x];
      if (activeGuideline) {
        activeGuidelines.push(activeGuideline);
      }
    }

    for (const y of [top, yCenter, bottom]) {
      const activeGuideline = this.guidelines.horizontal[y];
      if (activeGuideline) {
        activeGuidelines.push(activeGuideline);
      }
    }

    return activeGuidelines;
  };

  private setupGuidelines = () => {
    const aspectRatio =
      this.editorStore?.getState().video?.compositionJson.aspectRatio;

    if (!aspectRatio) {
      return;
    }

    const guidelines: Guidelines = {
      vertical: {},
      horizontal: {},
    };

    for (const elementId in this.get().boundingBoxes) {
      if (this.get().selectedElementIds.has(elementId)) {
        continue;
      }

      const boundingBox = this.get().boundingBoxes[elementId]!;

      const { left, right, top, bottom } = boundingBox;

      const hCenter = Math.floor((left + right) / 2);
      const vCenter = Math.floor((top + bottom) / 2);

      const vGuidelines = [left, hCenter, right];
      const hGuidelines = [top, vCenter, bottom];

      for (const x of vGuidelines) {
        const existingGuideline = guidelines.vertical[x];

        const newGuideline: VerticalGuideline = {
          type: "VERTICAL",
          x,
          top: existingGuideline ? Math.min(existingGuideline.top, top) : top,
          bottom: existingGuideline
            ? Math.max(existingGuideline.bottom, bottom)
            : bottom,
          sourceType: "ELEMENT",
        };

        guidelines.vertical[x] = newGuideline;
      }

      for (const y of hGuidelines) {
        const existingGuideline = guidelines.horizontal[y];

        const newGuideline: HorizontalGuideline = {
          type: "HORIZONTAL",
          y,
          left: existingGuideline
            ? Math.min(existingGuideline.left, left)
            : left,
          right: existingGuideline
            ? Math.max(existingGuideline.right, right)
            : right,
          sourceType: "ELEMENT",
        };

        guidelines.horizontal[y] = newGuideline;
      }
    }

    const resolution = getResolution(aspectRatio);

    for (const x of [0, Math.floor(resolution.width / 2), resolution.width]) {
      const newGuideline: VerticalGuideline = {
        type: "VERTICAL",
        x,
        top: 0,
        bottom: resolution.height,
        sourceType: "GLOBAL",
      };

      guidelines.vertical[x] = newGuideline;
    }

    for (const y of [0, Math.floor(resolution.height / 2), resolution.height]) {
      const newGuideline: HorizontalGuideline = {
        type: "HORIZONTAL",
        y,
        left: 0,
        right: resolution.width,
        sourceType: "GLOBAL",
      };

      guidelines.horizontal[y] = newGuideline;
    }

    this.guidelines = guidelines;
  };

  setSelectionFullscreen = () => {
    const selectedElementIds = this.get().selectedElementIds;
    const updateElement = this.editorStore?.getState().service.updateElement;
    const aspectRatio =
      this.editorStore?.getState().video?.compositionJson.aspectRatio;

    if (
      selectedElementIds.size !== 1 ||
      this.pointerState !== null ||
      !updateElement ||
      !aspectRatio
    ) {
      return;
    }

    const elementsMap = this.getElementsMapWithSceneAndIndex();
    const element = elementsMap[Array.from(selectedElementIds)[0]!];

    if (!element || !("size" in element.element)) {
      return;
    }

    const resolution = getResolution(aspectRatio);

    updateElement(
      element.element.externalId,
      (updatedElement) => {
        if (!("size" in updatedElement)) {
          throw new Error("Element size not found");
        }

        const currentWidth = updatedElement.size.w;
        const currentHeight = updatedElement.size.h;

        const aspectRatioElement = currentWidth / currentHeight;
        const aspectRatioResolution = resolution.width / resolution.height;

        let newWidth: number;
        let newHeight: number;

        if (updatedElement.type === "RECTANGLE") {
          newWidth = resolution.width;
          newHeight = resolution.height;
        } else if (aspectRatioElement > aspectRatioResolution) {
          newHeight = resolution.height;
          newWidth = newHeight * aspectRatioElement;
        } else {
          newWidth = resolution.width;
          newHeight = newWidth / aspectRatioElement;
        }

        newWidth = Math.round(newWidth);
        newHeight = Math.round(newHeight);

        const x = Math.round((resolution.width - newWidth) / 2);
        const y = Math.round((resolution.height - newHeight) / 2);

        updatedElement.position.x = x;
        updatedElement.position.y = y;
        updatedElement.size.w = newWidth;
        updatedElement.size.h = newHeight;
      },
      true,
      false,
    );
  };

  private handlePointerDownResize = (
    x: number,
    y: number,
    event: PointerEvent,
  ) => {
    const handleType = this.getTargetResizeHandleType(event);

    if (handleType === null) {
      return false;
    }

    const initialSelectionBoundingBox = this.getSelectionBoundingBox();

    if (initialSelectionBoundingBox === null) {
      return true;
    }

    this.updatePointerState({
      type: "POINTER_DOWN_RESIZE",
      initialCoordinates: { x, y },
      resizeHandleType: handleType,
      initialSelectionBoundingBox,
    });

    this.setupGuidelines();

    return true;
  };

  private updatePointerState = (
    pointerState: PointerState | null,
    shouldUpdateReactive: boolean = true,
  ) => {
    this.pointerState = pointerState;

    if (!shouldUpdateReactive) {
      return;
    }

    const reactive =
      pointerState === null
        ? null
        : {
            type: pointerState.type,
            resizeHandleType: pointerState.resizeHandleType,
          };

    this.set((state) => {
      state.reactivePointerState = reactive;
    });
  };

  private handlePointerMoveResize = (
    x: number,
    y: number,
    event: PointerEvent,
  ): boolean => {
    if (
      this.pointerState?.type !== "POINTER_DOWN_RESIZE" &&
      this.pointerState?.type !== "POINTER_MOVE_RESIZE"
    ) {
      return false;
    }

    const { x: initialX, y: initialY } = this.pointerState!.initialCoordinates;

    const offsetX = x - initialX;
    const offsetY = y - initialY;

    if (this.pointerState!.type === "POINTER_DOWN_RESIZE") {
      const isDragging = Math.abs(offsetX) > 10 || Math.abs(offsetY) > 10;

      if (isDragging) {
        this.updatePointerState({
          ...this.pointerState!,
          type: "POINTER_MOVE_RESIZE",
        });

        this.setupMovingElements();
      } else {
        return true;
      }
    }

    const selectionBox = this.calculateSelectionBoxResize(x, y, event);

    if (!selectionBox) {
      return true;
    }

    this.calculateMovingElementsFromSelectionBox(selectionBox);

    return true;
  };

  private handlePointerDownDrag = (
    x: number,
    y: number,
    event: PointerEvent,
  ) => {
    const { selectedElementId, isInitialElementAlreadySelected } =
      this.handleSelectElement(x, y, event);

    const isInSelectionBoundingBox = this.getIsCoordinateInSelectionBoundingBox(
      x,
      y,
    );

    if (isInSelectionBoundingBox) {
      this.handlePointerDownDragSelection(
        x,
        y,
        selectedElementId,
        isInitialElementAlreadySelected,
      );
    } else {
      this.handlePointerDownDragBlank(x, y, event);
    }
  };

  private handlePointerMoveDrag = (
    x: number,
    y: number,
    event: PointerEvent,
  ) => {
    if (
      this.pointerState?.type === "POINTER_DOWN_SELECTION" ||
      this.pointerState?.type === "POINTER_MOVE_SELECTION"
    ) {
      this.handlePointerMoveDragSelection(x, y, event);
    } else if (
      this.pointerState?.type === "POINTER_DOWN_BLANK" ||
      this.pointerState?.type === "POINTER_MOVE_BLANK"
    ) {
      this.handlePointerMoveDragBlank(x, y);
    }
  };

  private handlePointerDownDragSelection = (
    x: number,
    y: number,
    selectedElementId: string | null,
    isInitialElementAlreadySelected: boolean,
  ) => {
    const initialSelectionBoundingBox = this.getSelectionBoundingBox();

    if (initialSelectionBoundingBox === null) {
      return;
    }

    this.updatePointerState({
      type: "POINTER_DOWN_SELECTION",
      initialCoordinates: { x, y },
      initialElementId: selectedElementId,
      isInitialElementAlreadySelected,
      initialSelectionBoundingBox,
    });
    this.setupGuidelines();
  };

  private handlePointerDownDragBlank = (
    x: number,
    y: number,
    event: PointerEvent,
  ) => {
    const isShiftPressed = event.shiftKey;

    if (!isShiftPressed) {
      this.updateSelectedElementIds([], "replace");
    }

    this.updatePointerState({
      type: "POINTER_DOWN_BLANK",
      initialCoordinates: { x, y },
      selectedElementIdsAtStart: new Set(this.get().selectedElementIds),
    });
  };

  private handlePointerMoveDragSelection = (
    x: number,
    y: number,
    event: PointerEvent,
  ) => {
    const { x: initialX, y: initialY } = this.pointerState!.initialCoordinates;

    const offsetX = x - initialX;
    const offsetY = y - initialY;

    if (this.pointerState!.type === "POINTER_DOWN_SELECTION") {
      const isDragging = Math.abs(offsetX) > 10 || Math.abs(offsetY) > 10;

      if (isDragging) {
        this.updatePointerState({
          ...this.pointerState!,
          type: "POINTER_MOVE_SELECTION",
        });

        this.setupMovingElements();
      } else {
        return;
      }
    }

    let selectionBox = this.calculateSelectionBoxDrag(x, y, event);

    if (!selectionBox) {
      return;
    }

    selectionBox = this.snapSelectionToGuidelines(selectionBox);

    this.calculateMovingElementsFromSelectionBox(selectionBox);
  };

  private handlePointerMoveDragBlank = (x: number, y: number) => {
    const { x: initialX, y: initialY } = this.pointerState!.initialCoordinates;

    const offsetX = x - initialX;
    const offsetY = y - initialY;

    if (this.pointerState!.type === "POINTER_DOWN_BLANK") {
      const isDragging = Math.abs(offsetX) > 10 || Math.abs(offsetY) > 10;

      if (isDragging) {
        this.updatePointerState({
          ...this.pointerState!,
          type: "POINTER_MOVE_BLANK",
        });
      } else {
        return;
      }
    }

    const left = offsetX >= 0 ? initialX : initialX + offsetX;
    const right = left + Math.abs(offsetX);
    const top = offsetY >= 0 ? initialY : initialY + offsetY;
    const bottom = top + Math.abs(offsetY);

    this.set((state) => {
      state.marqueeBoundingBox = {
        left,
        right,
        top,
        bottom,
      };
    });

    const elementIdsInMarquee = this.getElementIdsInMarquee();

    this.set((state) => {
      const newSelectedElementIds = new Set([
        ...(this.pointerState?.selectedElementIdsAtStart ?? new Set()),
        ...elementIdsInMarquee,
      ]);

      state.selectedElementIds = newSelectedElementIds;
    });
  };

  private commitMovingElements = () => {
    const editorState = this.editorStore?.getState();

    if (!editorState) {
      return;
    }

    const updateElement = editorState.service.updateElement;
    const syncVideoDebounced = editorState.service.syncVideoDebounced;
    const movingElements = this.get().movingElements;

    for (const elementExternalId in movingElements) {
      const movingElement = movingElements[elementExternalId]!;

      updateElement(
        elementExternalId,
        (element: ElementDto) => {
          if ("typography" in element) {
            element.typography.fontSize = movingElement.fontSize;
          }

          if ("size" in element) {
            element.size.w = movingElement.w;
            element.size.h = movingElement.h;
          }

          element.position.x = Math.round(movingElement.x);
          element.position.y = Math.round(movingElement.y);
        },
        false,
        false,
      );
    }

    syncVideoDebounced();
  };

  private handlePointerUpDownSelection = (event: PointerEvent) => {
    const isShiftPressed = event.shiftKey;

    if (isShiftPressed) {
      if (
        this.pointerState?.isInitialElementAlreadySelected &&
        this.pointerState?.initialElementId
      ) {
        const initialElementId = this.pointerState!.initialElementId!;

        this.set((state) => {
          state.selectedElementIds.delete(initialElementId);
        });
      }

      return;
    }

    const newSelectedElementIds = this.pointerState!.initialElementId
      ? [this.pointerState!.initialElementId!]
      : [];

    this.updateSelectedElementIds(newSelectedElementIds, "replace");
  };

  private handleEditorPanelChange = () => {
    if (
      this.pointerState?.type !== "POINTER_DOWN_SELECTION" &&
      this.pointerState?.type !== "POINTER_MOVE_BLANK"
    ) {
      return;
    }

    const selectedElementIds = this.get().selectedElementIds;
    const editorService = this.editorStore?.getState().service;

    if (!editorService) {
      return;
    }

    // Multi-selecting on the canvas while editing a single element returns to the list.
    if (selectedElementIds.size > 1) {
      const overlaysTabState = this.editorStore!.getState().overlaysTabState;

      if (overlaysTabState.currentElement !== null) {
        editorService.updateOverlaysTabState((state: any) => {
          state.currentElement = null;
        });
      }

      return;
    }

    if (selectedElementIds.size !== 1) {
      return;
    }

    const elementId = Array.from(selectedElementIds)[0]!;
    const elementsMap = this.getElementsMapWithSceneAndIndex();

    const elementWithMeta = elementsMap[elementId];

    if (!elementWithMeta) {
      return;
    }

    editorService.updateOverlaysTabState((state: any) => {
      state.currentTab =
        elementWithMeta.sceneType === "mainScene"
          ? "elements"
          : elementWithMeta.sceneType;
      state.currentElement = {
        externalId: elementId,
        sceneType: elementWithMeta.sceneType,
      };
    });

    editorService.updateEditorPanelState((state: any) => {
      state.currentTab = "overlays";
    });
  };

  private handleSelectElement = (x: number, y: number, event: PointerEvent) => {
    const elementsMap = this.getElementsMapWithSceneAndIndex();
    const selectedElementId = this.getElementIdInCoordinate(x, y, elementsMap);

    if (selectedElementId === null) {
      return {
        selectedElementId: null,
        isInitialElementAlreadySelected: false,
      };
    }

    const isInitialElementAlreadySelected =
      this.get().selectedElementIds.has(selectedElementId);

    const isShiftPressed = event.shiftKey;

    const mode =
      isShiftPressed || isInitialElementAlreadySelected ? "merge" : "replace";

    this.updateSelectedElementIds([selectedElementId], mode);

    return {
      selectedElementId,
      isInitialElementAlreadySelected,
    };
  };

  private calculateSelectionBoxDrag = (
    x: number,
    y: number,
    event: PointerEvent,
  ): BoundingBox | null => {
    const { initialSelectionBoundingBox, initialCoordinates } =
      this.pointerState!;

    if (!initialSelectionBoundingBox) {
      return null;
    }

    const { x: initialX, y: initialY } = initialCoordinates;

    let offsetX = x - initialX;
    let offsetY = y - initialY;

    const isShiftPressed = event.shiftKey;

    if (isShiftPressed) {
      offsetX = Math.abs(offsetX) >= Math.abs(offsetY) ? offsetX : 0;
      offsetY = Math.abs(offsetY) > Math.abs(offsetX) ? offsetY : 0;
    }

    return {
      left: Math.round(initialSelectionBoundingBox.left + offsetX),
      right: Math.round(initialSelectionBoundingBox.right + offsetX),
      top: Math.round(initialSelectionBoundingBox.top + offsetY),
      bottom: Math.round(initialSelectionBoundingBox.bottom + offsetY),
    };
  };

  private calculateSelectionBoxResize = (
    x: number,
    y: number,
    event: PointerEvent,
  ): BoundingBox | null => {
    const { initialSelectionBoundingBox } = this.pointerState!;

    if (!initialSelectionBoundingBox) {
      return null;
    }

    const { offsetX, offsetY, isLeft, isTop, isEdge } = this.getResizeOffsets(
      x,
      y,
    );

    const isShiftPressed = event.shiftKey;

    const initialWidth =
      initialSelectionBoundingBox.right - initialSelectionBoundingBox.left;
    const initialHeight =
      initialSelectionBoundingBox.bottom - initialSelectionBoundingBox.top;

    let widthMultiplier = (initialWidth + offsetX) / initialWidth;
    let heightMultiplier = (initialHeight + offsetY) / initialHeight;

    if (!isShiftPressed && !isEdge) {
      const multiplier = (widthMultiplier + heightMultiplier) / 2;
      widthMultiplier = multiplier;
      heightMultiplier = multiplier;
    }

    const selectionWidth = Math.round(initialWidth * widthMultiplier);
    const selectionHeight = Math.round(initialHeight * heightMultiplier);

    if (selectionWidth < 1 || selectionHeight < 1) {
      return null;
    }

    const widthChange = selectionWidth - initialWidth;
    const heightChange = selectionHeight - initialHeight;

    const left = isLeft
      ? initialSelectionBoundingBox.left - widthChange
      : initialSelectionBoundingBox.left;
    const top = isTop
      ? initialSelectionBoundingBox.top - heightChange
      : initialSelectionBoundingBox.top;
    const right = isLeft
      ? initialSelectionBoundingBox.right
      : initialSelectionBoundingBox.right + widthChange;
    const bottom = isTop
      ? initialSelectionBoundingBox.bottom
      : initialSelectionBoundingBox.bottom + heightChange;

    return {
      left: Math.round(left),
      top: Math.round(top),
      right: Math.round(right),
      bottom: Math.round(bottom),
    };
  };

  private calculateSelectionBoxArrow = (): BoundingBox | null => {
    const { initialSelectionBoundingBox, latestCoordinates } =
      this.pointerState!;

    if (!initialSelectionBoundingBox || !latestCoordinates) {
      return null;
    }

    return {
      left: Math.round(initialSelectionBoundingBox.left + latestCoordinates.x),
      right: Math.round(
        initialSelectionBoundingBox.right + latestCoordinates.x,
      ),
      top: Math.round(initialSelectionBoundingBox.top + latestCoordinates.y),
      bottom: Math.round(
        initialSelectionBoundingBox.bottom + latestCoordinates.y,
      ),
    };
  };

  private calculateMovingElementsFromSelectionBox = (
    selectionBox: BoundingBox,
  ) => {
    const movingElementOffsets =
      this.calculateMovingElementOffsets(selectionBox);

    this.set((state) => {
      for (const elementId in state.movingElements) {
        const movingElement = state.movingElements[elementId]!;

        const scalingOffsets = this.calculateMovingElementScalingOffsets(
          selectionBox,
          movingElement,
        );

        movingElement.x = Math.round(
          movingElement.initialX +
            movingElementOffsets.xOffset +
            scalingOffsets.xOffset,
        );
        movingElement.y = Math.round(
          movingElement.initialY +
            movingElementOffsets.yOffset +
            scalingOffsets.yOffset,
        );

        movingElement.w = Math.round(
          movingElement.initialW * scalingOffsets.widthMultiplier,
        );
        movingElement.h = Math.round(
          movingElement.initialH * scalingOffsets.heightMultiplier,
        );

        const fontSize = Math.round(
          movingElement.initialFontSize * scalingOffsets.widthMultiplier,
        );
        movingElement.fontSize = Math.max(5, fontSize);
      }
    });
  };

  private calculateMovingElementOffsets = (
    selectionBox: BoundingBox,
  ): { xOffset: number; yOffset: number } => {
    const { left, top } = selectionBox;
    const { initialSelectionBoundingBox, type } = this.pointerState!;

    if (
      !initialSelectionBoundingBox ||
      !["POINTER_MOVE_SELECTION", "ARROW_MOVE_SELECTION"].includes(type)
    ) {
      return { xOffset: 0, yOffset: 0 };
    }

    const offsetX = left - initialSelectionBoundingBox.left;
    const offsetY = top - initialSelectionBoundingBox.top;

    return {
      xOffset: offsetX,
      yOffset: offsetY,
    };
  };

  private calculateMovingElementScalingOffsets = (
    selectionBox: BoundingBox,
    movingElement: MovingElement,
  ): {
    xOffset: number;
    yOffset: number;
    widthMultiplier: number;
    heightMultiplier: number;
  } => {
    const { left, top, right, bottom } = selectionBox;
    const { initialSelectionBoundingBox, resizeHandleType, type } =
      this.pointerState!;

    if (
      !initialSelectionBoundingBox ||
      !resizeHandleType ||
      type !== "POINTER_MOVE_RESIZE"
    ) {
      return {
        xOffset: 0,
        yOffset: 0,
        widthMultiplier: 1,
        heightMultiplier: 1,
      };
    }

    const widthMultiplier =
      (right - left) /
      (initialSelectionBoundingBox.right - initialSelectionBoundingBox.left);
    const heightMultiplier =
      (bottom - top) /
      (initialSelectionBoundingBox.bottom - initialSelectionBoundingBox.top);

    let xOffset: number;
    let yOffset: number;

    const isLeft = ["top-left", "bottom-left", "left"].includes(
      resizeHandleType,
    );
    const isTop = ["top-left", "top-right", "top"].includes(resizeHandleType);

    if (isLeft) {
      const offsetSelectionRight =
        initialSelectionBoundingBox.right - movingElement.initialX;

      xOffset = Math.round(offsetSelectionRight * (1 - widthMultiplier));
    } else {
      const offsetSelectionLeft =
        initialSelectionBoundingBox.left - movingElement.initialX;

      xOffset = Math.round(offsetSelectionLeft * (1 - widthMultiplier));
    }

    if (isTop) {
      const offsetSelectionBottom =
        initialSelectionBoundingBox.bottom - movingElement.initialY;

      yOffset = Math.round(offsetSelectionBottom * (1 - heightMultiplier));
    } else {
      const offsetSelectionTop =
        initialSelectionBoundingBox.top - movingElement.initialY;

      yOffset = Math.round(offsetSelectionTop * (1 - heightMultiplier));
    }

    return {
      xOffset,
      yOffset,
      widthMultiplier,
      heightMultiplier,
    };
  };

  private snapSelectionToGuidelines = (selectionBoundingBox: BoundingBox) => {
    if (!this.guidelines) {
      return selectionBoundingBox;
    }

    const { left, top, right, bottom } = selectionBoundingBox;
    const xCenter = Math.floor((left + right) / 2);
    const yCenter = Math.floor((top + bottom) / 2);

    const SNAP_THRESHOLD = 10;

    let closestVerticalGuideline: VerticalGuideline | null = null;
    let closestVerticalDistance = Infinity;
    let verticalSnapPoint = 0;

    for (const x of [left, xCenter, right]) {
      for (let offset = -SNAP_THRESHOLD; offset <= SNAP_THRESHOLD; offset++) {
        const guideline = this.guidelines.vertical[x + offset];
        if (guideline) {
          const distance = Math.abs(offset);
          if (distance < closestVerticalDistance) {
            closestVerticalDistance = distance;
            closestVerticalGuideline = guideline;
            verticalSnapPoint = x;
          }
        }
      }
    }

    let closestHorizontalGuideline: HorizontalGuideline | null = null;
    let closestHorizontalDistance = Infinity;
    let horizontalSnapPoint = 0;

    for (const y of [top, yCenter, bottom]) {
      for (let offset = -SNAP_THRESHOLD; offset <= SNAP_THRESHOLD; offset++) {
        const guideline = this.guidelines.horizontal[y + offset];
        if (guideline) {
          const distance = Math.abs(offset);
          if (distance < closestHorizontalDistance) {
            closestHorizontalDistance = distance;
            closestHorizontalGuideline = guideline;
            horizontalSnapPoint = y;
          }
        }
      }
    }

    let xOffset = 0;
    let yOffset = 0;

    if (closestVerticalGuideline) {
      xOffset = closestVerticalGuideline.x - verticalSnapPoint;
    }

    if (closestHorizontalGuideline) {
      yOffset = closestHorizontalGuideline.y - horizontalSnapPoint;
    }

    return {
      left: selectionBoundingBox.left + xOffset,
      right: selectionBoundingBox.right + xOffset,
      top: selectionBoundingBox.top + yOffset,
      bottom: selectionBoundingBox.bottom + yOffset,
    };
  };

  private getScaledCoordinate = (event: {
    clientX: number;
    clientY: number;
  }) => {
    const scale = this.editorStore
      ?.getState()
      .service.getPlayerRef()
      ?.current?.getScale();

    if (scale === undefined) {
      return null;
    }

    const { clientX, clientY } = event;

    const compositionRoot = document.getElementById(ELEMENT_CONTROLS_ROOT_ID);

    if (!compositionRoot) {
      return null;
    }

    const { left, top } = compositionRoot.getBoundingClientRect();

    return {
      x: (clientX - left) / scale,
      y: (clientY - top) / scale,
    };
  };

  private clearSelectionState = () => {
    this.guidelines = null;
    this.pointerState = null;

    if (this.arrowMoveTimeout !== null) {
      clearTimeout(this.arrowMoveTimeout);
      this.arrowMoveTimeout = null;
    }

    this.set((state) => {
      state.reactivePointerState = null;
      state.movingElements = {};
      state.selectedElementIds = new Set();
      state.marqueeBoundingBox = null;
    });
  };

  updateSelectedElementIds = (
    newElementIds: string[],
    mode: "merge" | "replace" | "toggle",
  ) => {
    this.set((state) => {
      if (mode === "merge") {
        state.selectedElementIds = new Set([
          ...state.selectedElementIds,
          ...newElementIds,
        ]);
      } else if (mode === "toggle") {
        const next = new Set(state.selectedElementIds);
        for (const id of newElementIds) {
          if (next.has(id)) next.delete(id);
          else next.add(id);
        }
        state.selectedElementIds = next;
      } else {
        state.selectedElementIds = new Set(newElementIds);
      }
    });
  };

  private getTargetResizeHandleType = (
    event: PointerEvent,
  ): ResizeHandleType | null => {
    if (!(event.target instanceof HTMLElement)) {
      return null;
    }

    const isResizeHandle = event.target.id.startsWith(RESIZE_HANDLE_ID_PREFIX);

    if (!isResizeHandle) {
      return null;
    }

    const type = event.target.id.replace(
      RESIZE_HANDLE_ID_PREFIX,
      "",
    ) as ResizeHandleType;

    if (!RESIZE_HANDLE_TYPES.includes(type)) {
      return null;
    }

    return type;
  };

  private getElementIdInCoordinate = (
    x: number,
    y: number,
    elementsMap: Record<string, ElementWithMeta>,
  ) => {
    const matchingElementIds = [];

    const boundingBoxes = this.get().boundingBoxes;

    for (const externalId in boundingBoxes) {
      const boundingBox = boundingBoxes[externalId]!;

      const isInside =
        boundingBox.left <= x &&
        boundingBox.right >= x &&
        boundingBox.top <= y &&
        boundingBox.bottom >= y;

      if (isInside) {
        matchingElementIds.push(externalId);
      }
    }

    matchingElementIds.sort((a, b) => {
      return elementsMap[a]!.index - elementsMap[b]!.index;
    });

    return matchingElementIds[0] ?? null;
  };

  private getIsCoordinateInSelectionBoundingBox = (x: number, y: number) => {
    const boundingBox = this.getSelectionBoundingBox();

    if (!boundingBox) {
      return false;
    }

    return (
      y >= boundingBox.top &&
      y <= boundingBox.bottom &&
      x >= boundingBox.left &&
      x <= boundingBox.right
    );
  };

  private getElementIdsInMarquee = (): Set<string> => {
    const marqueeBoundingBox = this.get().marqueeBoundingBox;

    if (!marqueeBoundingBox) {
      return new Set();
    }

    const elementIds: Set<string> = new Set();

    for (const id in this.get().boundingBoxes) {
      const boundingBox = this.get().boundingBoxes[id];

      if (!boundingBox) {
        continue;
      }

      const isOverlapping = !(
        marqueeBoundingBox.right <= boundingBox.left ||
        marqueeBoundingBox.left >= boundingBox.right ||
        marqueeBoundingBox.bottom <= boundingBox.top ||
        marqueeBoundingBox.top >= boundingBox.bottom
      );

      if (isOverlapping) {
        elementIds.add(id);
      }
    }

    return elementIds;
  };

  private getResizeOffsets = (x: number, y: number) => {
    let offsetX = x - this.pointerState!.initialCoordinates.x;
    let offsetY = y - this.pointerState!.initialCoordinates.y;

    const handleType = this.pointerState!.resizeHandleType!;

    const isLeft = ["top-left", "bottom-left", "left"].includes(handleType);
    const isTop = ["top-left", "top-right", "top"].includes(handleType);

    offsetX = isLeft ? -offsetX : offsetX;
    offsetY = isTop ? -offsetY : offsetY;

    const isEdgeHorizontal = ["left", "right"].includes(handleType);
    const isEdgeVertical = ["top", "bottom"].includes(handleType);

    if (isEdgeHorizontal) {
      offsetY = 0;
    }
    if (isEdgeVertical) {
      offsetX = 0;
    }

    return {
      offsetX,
      offsetY,
      isLeft,
      isTop,
      isEdge: isEdgeHorizontal || isEdgeVertical,
    };
  };

  private setupMovingElements = () => {
    const elementsMap = this.getElementsMapWithSceneAndIndex();
    const selectedElementIds = this.get().selectedElementIds;
    const boundingBoxes = this.get().boundingBoxes;

    const newMovingElements: Record<string, MovingElement> = {};

    for (const id of selectedElementIds) {
      const boundingBox = boundingBoxes[id];
      const element = elementsMap[id]?.element;

      if (!boundingBox || !element) {
        continue;
      }

      const initialX = boundingBox.left;
      const initialY = boundingBox.top;

      const initialW = Math.max(0, boundingBox.right - boundingBox.left);
      const initialH = Math.max(0, boundingBox.bottom - boundingBox.top);

      const initialFontSize =
        element.type === "TEXT" ? element.typography.fontSize : 0;

      newMovingElements[id] = {
        elementType: element.type,

        x: initialX,
        y: initialY,
        initialX,
        initialY,

        w: initialW,
        h: initialH,
        initialW,
        initialH,

        fontSize: initialFontSize,
        initialFontSize,
      };
    }

    this.set((state) => {
      state.movingElements = newMovingElements;
    });
  };

  private getIsAllowedPointerDownTarget = (event: PointerEvent) => {
    if (!(event.target instanceof HTMLElement)) {
      return false;
    }

    const videoPreviewContainer = document.getElementById(
      VIDEO_PREVIEW_CONTAINER_ID,
    );

    const aspectRatioControls = document.getElementById(
      ASPECT_RATIO_CONTROLS_ID,
    );

    const videoPreviewControls = document.getElementById(
      VIDEO_PREVIEW_CONTROLS_ID,
    );

    const isInsideVideoPreviewContainer =
      videoPreviewContainer?.contains(event.target) ?? false;

    const isInsideAspectRatioControls =
      aspectRatioControls?.contains(event.target) ?? false;

    const isInsideVideoPreviewControls =
      videoPreviewControls?.contains(event.target) ?? false;

    return (
      isInsideVideoPreviewContainer &&
      !isInsideAspectRatioControls &&
      !isInsideVideoPreviewControls
    );
  };
}
